import React, { useRef, useState } from 'react';
import { motion, useInView, useMotionValue, useSpring } from 'framer-motion';
import { ArrowDown, ArrowUpRight, Github, Linkedin, Pause, Play } from 'lucide-react';
import { useMotionPreference } from '../hooks/useMotionPreference';
import { profileData } from '../utils/mockData';

// Decorative architecture study, not live infrastructure or telemetry.
function ArchitectureStudy({ immersive }) {
  const ref = useRef(null);
  const visible = useInView(ref);
  const [paused, setPaused] = useState(false);
  const reduceMotion = useMotionPreference();
  const targetX = useMotionValue(0);
  const targetY = useMotionValue(0);
  const rotateX = useSpring(targetX, { stiffness: 140, damping: 22 });
  const rotateY = useSpring(targetY, { stiffness: 140, damping: 22 });
  const resetTilt = () => { targetX.set(0); targetY.set(0); };
  const followPointer = (event) => {
    if (!immersive || paused || reduceMotion || event.pointerType !== 'mouse') return;
    const rect = event.currentTarget.getBoundingClientRect();
    targetX.set((.5 - (event.clientY - rect.top) / rect.height) * 7);
    targetY.set(((event.clientX - rect.left) / rect.width - .5) * 7);
  };
  return (
    <motion.div ref={ref} className={`architecture-study ${visible && !paused ? 'is-visible' : ''}`}
      onPointerMove={followPointer} onPointerLeave={resetTilt}
      style={immersive && !reduceMotion ? { rotateX, rotateY, transformPerspective: 900 } : undefined}>
      <div aria-hidden="true" className="study-artwork">
      {immersive && <div className="study-aura" />}
      <div className="study-heading"><span>PLATFORM / ARCHITECTURE</span><span>01 — 03</span></div>
      <svg viewBox="0 0 520 510" fill="none" className="architecture-drawing">
        <defs>
          <pattern id="architecture-grid" width="26" height="26" patternUnits="userSpaceOnUse">
            <circle cx="1" cy="1" r="1" fill="currentColor" opacity=".16" />
          </pattern>
          <linearGradient id="architecture-face" x1="80" y1="100" x2="440" y2="450" gradientUnits="userSpaceOnUse">
            <stop stopColor="currentColor" stopOpacity=".2" />
            <stop offset="1" stopColor="currentColor" stopOpacity=".025" />
          </linearGradient>
        </defs>
        <rect width="520" height="510" fill="url(#architecture-grid)" />
        <ellipse cx="260" cy="401" rx="204" ry="78" stroke="currentColor" opacity=".15" strokeDasharray="3 7" />
        <path d="M260 46V454 M34 264L486 264" stroke="currentColor" opacity=".12" strokeDasharray="3 7" />
        {[0, 1, 2].map((layer) => (
          <g key={layer} className={`architecture-layer layer-${layer}`}>
            <path d={`M260 ${98 + layer * 93}L438 ${184 + layer * 93}L260 ${270 + layer * 93}L82 ${184 + layer * 93}Z`} fill="url(#architecture-face)" stroke="currentColor" strokeWidth="1.2" />
            <path d={`M82 ${184 + layer * 93}V${201 + layer * 93}L260 ${287 + layer * 93}L438 ${201 + layer * 93}V${184 + layer * 93} M260 ${270 + layer * 93}V${287 + layer * 93}`} stroke="currentColor" opacity=".35" />
            <path d={`M171 ${141 + layer * 93}L349 ${227 + layer * 93} M349 ${141 + layer * 93}L171 ${227 + layer * 93}`} stroke="currentColor" opacity=".22" />
            <circle cx="260" cy={184 + layer * 93} r="7" fill="var(--bg-surface)" stroke="currentColor" />
            <circle cx="260" cy={184 + layer * 93} r="2.5" fill="currentColor" />
          </g>
        ))}
        <path className="architecture-signal" d="M260 184V370" stroke="currentColor" strokeWidth="2" strokeDasharray="4 15" />
        <path d="M349 141L413 110H476 M438 277H476 M171 413L107 453H44" stroke="currentColor" opacity=".55" />
        <g fill="currentColor" fontFamily="monospace" fontSize="10" letterSpacing="1.4">
          <text x="398" y="99">CLOUD</text>
          <text x="398" y="302">PLATFORM</text>
          <text x="44" y="475">AUTOMATION</text>
        </g>
      </svg>
      <div className="study-footer"><span className="study-marker" /><span>Complexity, made clear.</span><span>↗</span></div>
      </div>
      {immersive && !reduceMotion && <button type="button" className="study-motion-control" onClick={() => { setPaused(!paused); resetTilt(); }} aria-label={paused ? 'Play illustration animation' : 'Pause illustration animation'}>
        {paused ? <Play size={14} /> : <Pause size={14} />}<span>{paused ? 'Play motion' : 'Pause motion'}</span>
      </button>}
    </motion.div>
  );
}

export default function ArchitecturalHero({ immersive = false }) {
  const reduceMotion = useMotionPreference();
  return (
    <section className="architectural-hero" aria-labelledby="architectural-title">
      {immersive && <div className="hero-watermark" aria-hidden="true">AB.</div>}
      <div className="architectural-layout">
        <motion.div className="architectural-copy" initial={reduceMotion ? false : { opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.65, ease: 'easeOut' }}>
          <p className="architectural-eyebrow"><span /> Platform Engineering · Cloud · OpenShift · Automation</p>
          <p className="architectural-intro">I make complex infra boring</p>
          <h1 id="architectural-title">{profileData.name}</h1>
          <h2>{profileData.title}</h2>
          <p className="architectural-description">{profileData.description}</p>
          <div className="architectural-actions">
            {immersive ? <>
              <a href="#projects" className="architectural-primary" data-pointer-label="Explore">Explore my work <ArrowUpRight size={19} /></a>
              <a href="#contact" className="architectural-secondary">Let’s talk <ArrowUpRight size={18} /></a>
            </> : <>
            <a href={profileData.linkedin} target="_blank" rel="noopener noreferrer" className="architectural-primary"><Linkedin size={17} /> LinkedIn <ArrowUpRight size={17} /></a>
            <a href={profileData.github} target="_blank" rel="noopener noreferrer" className="architectural-secondary"><Github size={18} /> GitHub <ArrowUpRight size={16} /></a>
            </>}
          </div>
          {immersive && <div className="hero-socials"><span>Find me elsewhere</span><a href={profileData.linkedin} target="_blank" rel="noopener noreferrer"><Linkedin size={15} /> LinkedIn <ArrowUpRight size={13} /></a><a href={profileData.github} target="_blank" rel="noopener noreferrer"><Github size={15} /> GitHub <ArrowUpRight size={13} /></a></div>}
        </motion.div>
        <ArchitectureStudy immersive={immersive} />
      </div>
      <div className="architectural-bottom">
        <p>{profileData.subtitle}</p>
        <a href="#about">Explore portfolio <ArrowDown size={16} /></a>
      </div>
    </section>
  );
}
